import React, { useEffect, useRef, useState } from 'react';

const DURATION = 900;
// Same curve as the admin's count-up (cubic-bezier(0.22, 1, 0.36, 1), an ease-out quint)
const ease = (t: number) => 1 - Math.pow(1 - t, 5);

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Counts up from zero when it first appears, then tweens between values (e.g. when switching tries) */
export function AnimatedNumber({ value, digits = 0, suffix = '' }: { value: number; digits?: number; suffix?: string }) {
  const [display, setDisplay] = useState(() => (prefersReducedMotion() ? value : 0));
  const currentRef = useRef(display);

  useEffect(() => {
    if (prefersReducedMotion()) {
      currentRef.current = value;
      setDisplay(value);
      return;
    }
    // Start from wherever the number is now, so a repeated effect (development) still animates
    const from = currentRef.current;
    const start = performance.now();
    let frame = requestAnimationFrame(function step(now) {
      const t = Math.min(1, (now - start) / DURATION);
      const next = from + (value - from) * ease(t);
      currentRef.current = next;
      setDisplay(next);
      if (t < 1) frame = requestAnimationFrame(step);
    });
    return () => cancelAnimationFrame(frame);
  }, [value]);

  return (
    <span className="tnum">
      {display.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}
      {suffix}
    </span>
  );
}
